<template>
  <div>
    <v-container
      fluid
      style="background-color: #eee;"
    >
      <!-- 参照元 -->
      <v-row :dense="true">
        <v-col cols="3">
          <div class="pa-2">
            <b>参照元</b>
          </div>
        </v-col>
        <v-col
          cols="9"
          class="d-flex flex-column"
        >
          <div
            v-for="option in options.departmentTypes"
            :key="option.value"
          >
            <v-checkbox
              v-model="query.departmentTypes"
              :value="option.value"
              :label="option.text"
              density="compact"
              hide-details
            />
          </div>
        </v-col>
      </v-row>

      <!-- キーワード -->
      <v-row :dense="true">
        <v-col cols="3">
          <div class="pa-2">
            <b>キーワード</b>
          </div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="query.keyword"
            density="compact"
            hide-details
            class="pa-0"
            placeholder="スペース区切り キーワード"
          />
        </v-col>
      </v-row>

      <!-- 執筆者 -->
      <v-row :dense="true">
        <v-col cols="3">
          <div class="pa-2">
            <b>執筆者</b>
          </div>
        </v-col>
        <v-col cols="9">
          <v-text-field
            v-model="query.author"
            density="compact"
            hide-details
            class="pa-0"
            placeholder="執筆者名（部分一致）"
          />
          <a
            v-if="currentDoctor"
            class="mt-1"
            style="color: #999; font-size: small; display: flex; flex-direction: column; text-decoration: underline;"
            @click="query.author = currentDoctor?.name"
          >
            「{{ currentDoctor.name }}」を入力
          </a>
        </v-col>
      </v-row>

      <!-- 回答文を含む -->
      <v-row
        v-if="props.type === 'anytimeConsultation'"
        :dense="true"
      >
        <v-col cols="3">
          <div class="pa-2">
            <b>詳細設定</b>
          </div>
        </v-col>
        <v-col
          v-if="answerContent === null"
          cols="9"
        >
          <v-checkbox
            disabled
            label="作成中の回答文に近い記事を優先"
            density="compact"
            class="mr-3"
            hide-details
          />
          <span
            style="color: #999; font-size: small; display: flex; flex-direction: column; "
          >
            ※ 回答文が300文字以上必要です
          </span>
        </v-col>
        <v-col
          v-else
          cols="9"
        >
          <v-checkbox
            v-model="query.answerIncluded"
            label="作成中の回答文に近い記事を優先"
            density="compact"
            class="mr-3"
            hide-details
          />
          <span
            style="color: #999; font-size: small; display: flex; flex-direction: column; "
          >
            ※ 回答文が300文字以上必要です
          </span>
        </v-col>
      </v-row>

      <!-- 検索ボタン -->
      <v-row :dense="true">
        <v-col class="text-center">
          <v-btn
            color="grey-darken-4"
            density="comfortable"
            @click="updateList"
          >
            検索
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-divider />
    <div
      v-if="!hasOnceSearched || isJournalSearching"
      class="pa-4 text-center"
    >
      <v-progress-circular
        indeterminate
        color="grey"
      />
      <p class="pa-2">
        検索中...
      </p>
    </div>
    <div
      v-else-if="journals.length === 0"
      class="text-center"
    >
      <p class="pa-2">
        検索結果はありません。
      </p>
    </div>
    <div
      v-else
      class="px-5"
    >
      <div
        class="d-flex align-center mt-2"
        data-test="search-result-message"
      >
        {{ searchResultMessage }}
      </div>
      <div class="mt-3">
        <JournalElasticSearchResult
          v-for="journal in journals"
          :key="journal.id"
          :journal="journal"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted } from "vue";
import { useStore } from "vuex";
import JournalElasticSearchResult from "@/vue/components/shared/sidebar/Journal/JournalElasticSearchResult.vue";

// Props定義
const props = defineProps({
  type: {
    type: String,
    default: null,
    validator: (value) => {
      return value === null || ["anytimeConsultation", "reservation", "daytimeAdviceSheet"].includes(value);
    }
  }
});

const store = useStore();

const hasOnceSearched = ref(false);

const options = reactive({
  departmentTypes: [
    { text: "小児科ジャーナル", value: "pediatrician" },
    { text: "産婦人科ジャーナル", value: "obstetrician" },
  ],
});

const query = reactive({
  keyword: "",
  author: "",
  departmentTypes: ["pediatrician", "obstetrician"],
  answerIncluded: true
});

const journals = computed(() => store.getters.journals);
const currentDoctor = computed(() => store.getters.currentDoctor);
const isJournalSearching = computed(() => store.getters.isJournalSearching);
const previousQuery = computed(() => store.getters.previousQuery);
const anytimeConsultationId = computed(() => {
  return props.type === "anytimeConsultation" ? store.getters.anytimeConsultation?.id : null;
});
const reservationId = computed(() => {
  return props.type === "reservation" ? store.getters.reservation?.id : null;
});
const daytimeAdviceSheetId = computed(() => {
  return props.type === "daytimeAdviceSheet" ? store.getters.daytimeAdviceSheet?.id : null;
});
const answerContent = computed(() => {
  if (props.type && props.type == "reservation") {
    return null; // 夜間相談の場合は回答文をクエリに使用しない
  }
  if (props.type && props.type == "anytimeConsultation" && store.getters.anytimeConsultationAnswer?.content) {
    // 文字数が300文字を超える場合のみ、回答文を検索クエリとして使用する
    if (store.getters.anytimeConsultationAnswer.content.length > 300) {
      return store.getters.anytimeConsultationAnswer.content;
    }
    // それ以外の場合はnullを返す
    return null;
  }
  return null; // どの条件にも当てはまらない場合はnullを返す
});

// 検索結果の件数
const hit = computed(() => {
  return journals.value.length;
});

// 前回の検索で回答文が含まれていたかどうかを判定
const isPreviousSearchIncludedAnswer = computed(() => {
  return previousQuery.value?.query?.answerContent != null;
});

// 検索結果の表示文言を組み上げる
const searchResultMessage = computed(() => {
  const hitCount = hit.value >= 20 ? "上位20件を表示" : `${hit.value}ヒット`;
  if (props.type === "daytimeAdviceSheet" || props.type === "reservation") {
    return `検索結果/${hitCount}`;
  }
  const considerationType = isPreviousSearchIncludedAnswer.value ? "相談文と回答文を元にした検索結果" : "相談文を元にした検索結果";
  return `${considerationType}/${hitCount}`;
});

const updateList = () => {
  // いずれかのIDが必須
  if (!reservationId.value && !anytimeConsultationId.value && !daytimeAdviceSheetId.value) {
    return;
  }

  const queryPayload = {
    keyword: query.keyword,
    author: query.author,
    departmentTypes: query.departmentTypes,
    reservationId: reservationId.value,
    anytimeConsultationId: anytimeConsultationId.value,
    daytimeAdviceSheetId: daytimeAdviceSheetId.value,
    answerContent: query.answerIncluded ? answerContent.value : null
  };

  store.dispatch("elasticsearchJournals", { query: queryPayload })
    .then(() => {
      // 成功時の処理
    })
    .catch(() => {
      // エラー時の処理
    });

  hasOnceSearched.value = true;
};

const start = () => {
  updateList();
};
const clear = () => {
  query.keyword = "";
  query.author = "";
  query.departmentTypes = ["pediatrician", "obstetrician"];
  hasOnceSearched.value = false;
  store.dispatch("clearJournals");
};
onUnmounted(() => {
  clear();
});

// 親コンポーネントから呼び出せるようにメソッドを公開
defineExpose({
  start,
  clear
});
</script>

<style lang="scss" scoped></style>
